import React from "react";
import { CurrencyRupeeIcon } from "@heroicons/react/outline";
import NumberInput from "../../../../common/NumberInput";
import MetalMarginCard from "../components/MetalMarginCard";

const MarginsTab = ({ rateConfig, setRateConfig, updateMarginOverride, disabled, hybridConfig }) => {
  const isMetalDisabled = (metal) => {
    if (disabled) return true;
    if (rateConfig.ratePolicy === "HYBRID") {
      return hybridConfig.metalPolicies[metal] === "INHERIT" && !hybridConfig.customizeMargins;
    }
    return false;
  };

  const metals = [
    { id: 'gold', name: 'Gold', color: 'yellow', bgColor: 'bg-yellow-50' },
    { id: 'silver', name: 'Silver', color: 'gray', bgColor: 'bg-gray-50' },
    { id: 'platinum', name: 'Platinum', color: 'purple', bgColor: 'bg-purple-50' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-3">Metal-specific Margin Overrides</h3>
        <p className="text-sm text-gray-600 mb-4">
          Define custom margins for each metal type. Buy margins are typically negative.
        </p>

        {/* Gold, Silver, Platinum Margins */}
        {metals.map(metal => (
          <MetalMarginCard
            key={metal.id}
            metal={metal}
            sellMargin={rateConfig.marginOverrides[metal.id].sellMargin}
            buyMargin={rateConfig.marginOverrides[metal.id].buyMargin}
            onSellMarginChange={(value) => updateMarginOverride(metal.id, 'sellMargin', value)}
            onBuyMarginChange={(value) => updateMarginOverride(metal.id, 'buyMargin', value)}
            disabled={isMetalDisabled(metal.id)}
          />
        ))}

        {/* Diamond Margin */}
        <DiamondMarginCard
          margin={rateConfig.marginOverrides.diamond.margin}
          onChange={(value) => setRateConfig((prev) => ({
            ...prev,
            marginOverrides: {
              ...prev.marginOverrides,
              diamond: { margin: value },
            },
          }))}
          disabled={isMetalDisabled('diamond')}
        />

        {/* Margin Summary */}
        <MarginSummary marginOverrides={rateConfig.marginOverrides} />

        {/* Info Note */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> Margins are applied on top of base rates.
            Selling margins increase the selling price, while buying margins
            (usually negative) decrease the buying price. Diamond uses
            percentage-based margin.
          </p>
        </div>
      </div>
    </div>
  );
};

const DiamondMarginCard = ({ margin, onChange, disabled }) => (
  <div className="bg-blue-50 p-4 rounded-lg mb-4">
    <h4 className="font-medium mb-3 flex items-center">
      <CurrencyRupeeIcon className="h-5 w-5 mr-2 text-blue-600" />
      Diamond Margin
    </h4>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Margin (%)
      </label>
      <div className="max-w-xs">
        <NumberInput
          value={margin}
          onChange={onChange}
          suffix="%"
          step="0.5"
          placeholder="15"
          disabled={disabled}
        />
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Percentage margin applied on diamond prices
      </p>
    </div>
  </div>
);

const MarginSummary = ({ marginOverrides }) => (
  <div className="bg-indigo-50 p-4 rounded-lg">
    <h4 className="font-medium mb-3">Configured Margins Summary</h4>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
      <div>
        <p className="font-medium text-yellow-700">Gold</p>
        <p className="text-xs">Sell: +₹{Number(marginOverrides.gold.sellMargin) || 0}</p>
        <p className="text-xs">Buy: ₹{Number(marginOverrides.gold.buyMargin) || 0}</p>
      </div>
      <div>
        <p className="font-medium text-gray-700">Silver</p>
        <p className="text-xs">Sell: +₹{Number(marginOverrides.silver.sellMargin) || 0}</p>
        <p className="text-xs">Buy: ₹{Number(marginOverrides.silver.buyMargin) || 0}</p>
      </div>
      <div>
        <p className="font-medium text-purple-700">Platinum</p>
        <p className="text-xs">Sell: +₹{Number(marginOverrides.platinum.sellMargin) || 0}</p>
        <p className="text-xs">Buy: ₹{Number(marginOverrides.platinum.buyMargin) || 0}</p>
      </div>
      <div>
        <p className="font-medium text-blue-700">Diamond</p>
        <p className="text-xs">Margin: {Number(marginOverrides.diamond.margin) || 0}%</p>
      </div>
    </div>
  </div>
);

export default MarginsTab;
